import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconPlus } from '@tabler/icons-react';
import { callFn, StatusChip, fmtDate, inputStyle } from './contractsApi';

const STATUS_FILTERS = ['all', 'draft', 'pending', 'finalising', 'completed', 'voided', 'expired'];

export default function Contracts() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  useEffect(() => {
    callFn('listContracts', {})
      .then(r => setContracts(r.contracts))
      .catch(e => setError(e.message));
  }, []);

  const clients = useMemo(() => [...new Set((contracts || []).map(c => c.clientName))].sort(), [contracts]);
  const filtered = (contracts || []).filter(c =>
    (statusFilter === 'all' || c.status === statusFilter) &&
    (clientFilter === 'all' || c.clientName === clientFilter));

  return (
    <Layout>
      <PageHeader
        title="Contracts"
        subtitle={contracts ? `${contracts.filter(c => c.status === 'pending').length} awaiting signature · ${contracts.length} total` : 'Loading...'}
        actions={<Button icon={<IconPlus size={14} stroke={2} />} onClick={() => navigate('/contracts/new')}>New contract</Button>}
      />
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_FILTERS.map(s => {
            const active = statusFilter === s;
            return (
              <div key={s} onClick={() => setStatusFilter(s)} style={{
                padding: '5px 12px', borderRadius: 'var(--radius-full)', fontSize: 12, cursor: 'pointer',
                background: active ? 'var(--color-brand)' : 'var(--color-bg-primary)',
                color: active ? '#fff' : 'var(--color-text-secondary)',
                border: active ? 'none' : '0.5px solid var(--color-border-light)',
                textTransform: 'capitalize',
              }}>{s}</div>
            );
          })}
          <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} style={{ ...inputStyle, width: 200, height: 30, marginLeft: 'auto' }}>
            <option value="all">All clients</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {error && <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', marginBottom: 12, fontSize: 12 }}>{error}</div>}

        {contracts && filtered.length === 0 ? (
          <Card>
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {contracts.length === 0 ? 'No contracts yet.' : 'No contracts match these filters.'}
            </div>
          </Card>
        ) : contracts && (
          <Card padding={0}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 110px 100px 110px 100px', padding: '10px 16px', gap: 12, fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div>Title</div><div>Client</div><div>Progress</div><div>Status</div><div>Payment</div><div>Created</div>
            </div>
            {filtered.map((c, i) => (
              <div key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}
                style={{ display: 'grid', gridTemplateColumns: '1fr 180px 110px 100px 110px 100px', padding: '12px 16px', gap: 12, alignItems: 'center', fontSize: 12, cursor: 'pointer', borderBottom: i === filtered.length - 1 ? 'none' : '0.5px solid var(--color-border-light)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                <div style={{ color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.clientName}</div>
                <div style={{ color: 'var(--color-text-secondary)' }}>{c.signedCount} of {c.signerCount} signed</div>
                <div><StatusChip status={c.status} /></div>
                <div>{c.payment ? <StatusChip status={`pay_${c.payment.status}`} /> : <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>}</div>
                <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{fmtDate(c.createdAt)}</div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
}
